import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import Offer from "../../../../../utils/OfferClass/Offer/Offer";
import { OfferCardSize } from "../../../../../utils/enums";
import { localizedNumber } from "../../../../../utils/help/localization/localization";
import { capitalizeFirstLetter } from "../../../../../utils/help/textPreparation/textPreparation";
import ConfettiComponent from "../../../../global/AnimatedConffeti/AnimatedConfettiComponent";
import { AddIcon, Confetti, MinusIcon } from "../../../../global/Icons/Icons";
import {
  LockIconCircle,
  MileStone,
} from "../../../../global/Icons/loyalty.icons";
import { PChip } from "../../../../shared/PChip/PChip";
import { PImage } from "../../../../shared/PImage/PImage";
import GraphicsSlider from "./GraphicsSlider";
import styles from "./OfferCardV2.module.scss";
import { useOfferCard } from "./useOfferCard";

export const OfferCardV2 = (props: {
  offer: Offer;
  isActiveOffer: boolean;
  size: OfferCardSize;
  hideRedeemBtn?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  pointsToUnlock?: number;
  hasMinWidth?: boolean;
  /** Renders the Figma tier-surface treatment: drop shadow, no idle stroke. */
  tierSurface?: boolean;
}) => {
  const {
    offer,
    isActiveOffer,
    size,
    hideRedeemBtn,
    hasMinWidth = true,
    tierSurface = false,
  } = props;

  const {
    notApplicableOffer,
    unavailableOffer,
    outOfStockOffer,
    currentCardIndex,
    setCurrentCardIndex,
    isLocked,
    isMultiCard,
    isMainDark,
    addTwist,
    hasBottomSection,
    hasBackgroundText,
    offerCards,
    offerTitle,
    offerBgText,
    offerDetails,
    imageUrls,
    graphics,
    state,
    handleCardClick,
    createMarkup,
    outOfStockText,
    offerNotApplicable,
    offerUnavailableText,
  } = useOfferCard(props);

  const { t } = useTranslationI18();

  const chipSize = size === OfferCardSize.small ? "sm" : "md";

  const detailsChipText = outOfStockOffer
    ? outOfStockText
    : notApplicableOffer
    ? offerNotApplicable
    : unavailableOffer
    ? offerUnavailableText
    : offer.offerCardDetailsText({ short: true });

  const isUnavailable =
    notApplicableOffer || unavailableOffer || outOfStockOffer;

  const appliedText = t("common:offer-applied");

  return (
    <div
      data-testid={`cart-gift-card-v2-${offer.offerType}`}
      className={`${styles.gift_card} ${styles[OfferCardSize[size]]} ${
        isActiveOffer ? styles.active : ""
      } ${tierSurface ? styles.tier_surface : ""} ${
        isLocked || unavailableOffer ? styles.locked : ""
      } ${isLocked && isMainDark ? "bg__zero invert" : ""}`}
      style={{ ...(!hasMinWidth && { minWidth: "unset !important" }) }}
      onClick={handleCardClick}
      key={offer.id}
    >
      <div
        className={`${styles.main_section} ${
          !graphics && imageUrls ? styles.no_bg : ""
        }`}
      >
        <span className={styles.value}>
          {/* Floating "details" chip – top right */}
          {detailsChipText && !isMultiCard && (
            <PChip
              size={chipSize}
              variant={isUnavailable ? "alert" : "primary"}
              className={`${styles.details_chip} ${
                isUnavailable ? styles.chip_alert : ""
              }`}
            >
              {capitalizeFirstLetter(detailsChipText)}
            </PChip>
          )}

          {/* Floating +/- button — sits above bottom section */}
          {!hideRedeemBtn &&
            !notApplicableOffer &&
            !unavailableOffer &&
            !isLocked && (
              <div
                data-testid={`${state}-offer-v2`}
                className={`${styles.state} ${
                  isActiveOffer ? styles.active_state : ""
                }`}
              >
                {isActiveOffer ? <MinusIcon /> : <AddIcon />}
              </div>
            )}

          {isLocked ? (
            <div
              data-testid="lock-icon"
              className={`${styles.floating_button} ${styles.lock}`}
            >
              <LockIconCircle />
            </div>
          ) : null}

          <div className={styles.central_section}>
            <div className={styles.confetti}>
              <Confetti />
            </div>

            {addTwist && (
              <div className={styles.twist_confetti}>
                <ConfettiComponent twistIcon key={offer.id} />
              </div>
            )}

            <div className={styles.central_section_foreground}>
              {isMultiCard && (
                <div className={styles.pagination}>
                  {offerCards?.map((_, index: number) => (
                    <span
                      key={index}
                      className={
                        currentCardIndex === index ? styles.active : styles.none
                      }
                      onClick={() => setCurrentCardIndex(index)}
                    />
                  ))}
                </div>
              )}

              {hasBackgroundText && (
                <div className={styles.bg_text}>
                  {offerBgText?.map((item, index) => (
                    <span
                      key={index}
                      className={
                        item.useAlternateStyle ? styles.alternate_style : ""
                      }
                    >
                      <span dangerouslySetInnerHTML={createMarkup(item.text)} />
                    </span>
                  ))}
                </div>
              )}

              {isMultiCard ? (
                <GraphicsSlider
                  currentIndex={currentCardIndex}
                  setCurrentIndex={setCurrentCardIndex}
                  offerCards={offerCards}
                  large={
                    size === OfferCardSize.large ||
                    size === OfferCardSize.medium
                  }
                />
              ) : (
                imageUrls && (
                  <div className={styles.img_container}>
                    <PImage
                      src={imageUrls[0]}
                      alt=""
                      fill
                      className={styles.img}
                      style={{
                        objectFit: "cover",
                        objectPosition: "center",
                        overflow: "visible",
                      }}
                    />
                  </div>
                )
              )}

              {isActiveOffer && (
                <div
                  className={`${styles.green_tick}`}
                  data-testid="green-tick"
                >
                  <span>{appliedText}</span>
                </div>
              )}
            </div>
          </div>
        </span>

        {hasBottomSection && (
          <div className={styles.bottom_section}>
            {offer.milestonePoints && (
              <div className={styles.points}>
                <span>{localizedNumber(offer.milestonePoints)}</span>
                <MileStone />
              </div>
            )}
            <div className={styles.text_section}>
              <div
                className={`${styles.main_text} ${
                  offer.milestonePoints ? styles.tighter : ""
                }`}
              >
                {offerTitle?.map((item) => item.text).join(" ")}
              </div>
              {size === OfferCardSize.large && offerDetails && (
                <span className={styles.details_text}>{offerDetails}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
